// Éléments d'affichage d'une course (transport, lot T1a) : liens du vol et résumé pour la liste des courses.
// Espace chauffeur (lot T4a) : étape, Reçu, suivi pour le dispatch, correction, panneau du chauffeur.
import React, { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { fmtDate, fmtTime, money } from "@/lib/helpers";
import { useSettings } from "@/context/SettingsContext";
import { cn } from "@/lib/utils";
import { flightCode, flightradarUrl, NICE_ARRIVALS_URL, fleetName, rideKindOf, RIDE_KINDS, PAYERS, requestText, requestsOf,
  STEP_LABELS, STEP_ORDER, STEP_CLS, DRIVER_NEXT, UNDO_SECONDS, stepOf, waitMinutes, placeAddress, wazeUrl, mapsUrl, telHref } from "@/lib/ride";
import StatusBadge from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction } from "@/components/ui/alert-dialog";
import { Plane, Phone, Users, CarFront, MapPin, Check, Navigation, Undo2, Wallet } from "lucide-react";
import { toast } from "sonner";
import { formatPhone } from "@/lib/phone";

// Liens en un geste vers FlightRadar et l'aéroport de Nice, seulement si la référence ressemble à un n° de vol.
export function FlightLinks({ travelRef, testid = "flight-links" }) {
  const code = flightCode(travelRef);
  if (!code) return null;
  const lien = "inline-flex items-center gap-1 px-2 py-1 rounded-full border border-border text-xs hover:bg-muted whitespace-nowrap";
  return (
    <span className="inline-flex flex-wrap gap-1.5" data-testid={testid}>
      <a href={flightradarUrl(code)} target="_blank" rel="noopener noreferrer" className={lien} data-testid={`${testid}-fr24`}><Plane className="h-3 w-3" /> FlightRadar</a>
      <a href={NICE_ARRIVALS_URL} target="_blank" rel="noopener noreferrer" className={lien} data-testid={`${testid}-nice`}><Plane className="h-3 w-3 rotate-45" /> Arrivées Nice</a>
    </span>
  );
}

// Demandes spéciales d'une course (lot T1b-2) : une pastille par demande, « Siège bébé (2 ans) ».
export function RequestPills({ m, testid }) {
  const demandes = requestsOf(m);
  if (!demandes.length) return null;
  return (
    <div className="flex flex-wrap gap-1.5" data-testid={testid}>
      {demandes.map((r) => (
        <span key={r.key} className="px-2 py-0.5 text-xs rounded-full bg-sky-100 text-sky-800 dark:bg-sky-900/30 dark:text-sky-300 break-words max-w-full" data-testid={`${testid}-${r.key}`}>
          {requestText(r)}
        </span>
      ))}
    </div>
  );
}

// Détail d'une course dans la liste : type et horaires, trajet, passagers, passager (appel), véhicule, vol.
export function RideSummary({ m, fleet, places = [], employeeName }) {
  const v = fleet.find((x) => x.id === m.fleet_vehicle_id);
  // Lieux du carnet (lot T1b-1) : adresse complète et consignes d'accès, visibles aussi par le chauffeur.
  const lieux = [["pickup", m.pickup_place_id], ["dropoff", m.dropoff_place_id]]
    .map(([k, id]) => [k, id && places.find((p) => p.id === id)]).filter(([, p]) => p);
  const memeJour = fmtDate(m.start) === fmtDate(m.end);
  const kind = rideKindOf(m);
  const titre = kind === "hourly" && m.duration_hours ? `${RIDE_KINDS.hourly} ${String(m.duration_hours).replace(".", ",")} h` : (m.type || RIDE_KINDS[kind]);
  const trajet = [m.location, m.dropoff_location].map((x) => (x || "").trim()).filter(Boolean);
  const ligne = "flex flex-wrap items-center gap-x-3 gap-y-1";
  return (
    <div className="text-sm text-muted-foreground mt-1 space-y-1 min-w-0" data-testid={`ride-summary-${m.id}`}>
      <div className={ligne}>
        <span>{titre} · {fmtDate(m.start)} {fmtTime(m.start)} → {memeJour ? fmtTime(m.end) : `${fmtDate(m.end)} ${fmtTime(m.end)}`}</span>
        <span>{employeeName}</span>
      </div>
      {trajet.length > 0 && (
        <div className="flex items-start gap-1.5 text-foreground break-words" data-testid={`ride-route-${m.id}`}>
          <MapPin className="h-3.5 w-3.5 mt-0.5 shrink-0 text-accent" /><span className="min-w-0">{trajet.join(" → ")}</span>
        </div>
      )}
      {lieux.map(([k, p]) => (
        <div key={k} className="pl-5 text-xs break-words" data-testid={`ride-place-${k}-${m.id}`}>
          <span className="text-foreground">{p.name}</span> : {p.address}
          {p.access_notes && <span className="block whitespace-pre-line" data-testid={`ride-place-${k}-notes-${m.id}`}>Accès : {p.access_notes}</span>}
        </div>
      ))}
      <div className={ligne}>
        {m.passengers && <span className="inline-flex items-center gap-1 whitespace-nowrap" data-testid={`ride-pax-${m.id}`}><Users className="h-3.5 w-3.5" /> {m.passengers} {m.passengers > 1 ? "passagers" : "passager"}</span>}
        {m.passenger_name && <span className="break-words" data-testid={`ride-passenger-${m.id}`}>{m.passenger_name}</span>}
        {m.passenger_phone && (
          <a href={`tel:${m.passenger_phone.replace(/[^\d+]/g, "")}`} className="inline-flex items-center gap-1 text-accent whitespace-nowrap" data-testid={`ride-call-${m.id}`}>
            <Phone className="h-3.5 w-3.5" /> {formatPhone(m.passenger_phone)}
          </a>
        )}
        {m.payer === "passenger" && <span className="px-2 py-0.5 text-xs rounded-full bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300 whitespace-nowrap" data-testid={`ride-payer-${m.id}`}>Payé par le {PAYERS.passenger.toLowerCase()}</span>}
      </div>
      <RequestPills m={m} testid={`ride-requests-${m.id}`} />
      {(v || m.travel_ref) && (
        <div className={ligne}>
          {v && <span className="inline-flex items-center gap-1" data-testid={`ride-vehicle-${m.id}`}><CarFront className="h-3.5 w-3.5" /> {fleetName(v)}</span>}
          {m.travel_ref && <span className="inline-flex items-center gap-1 whitespace-nowrap" data-testid={`ride-travel-${m.id}`}><Plane className="h-3.5 w-3.5" /> {m.travel_ref}</span>}
          <FlightLinks travelRef={m.travel_ref} testid={`ride-flight-${m.id}`} />
        </div>
      )}
    </div>
  );
}

// ---------- Espace chauffeur (lot T4a) ----------

// Étape de la course (« Sur place », « Client absent »…) ; une course annulée garde la pastille « Annulée ».
export function StepBadge({ m }) {
  if (m.status === "cancelled") return <StatusBadge status="cancelled" />;
  const s = stepOf(m);
  return <span className={cn("inline-flex items-center px-2.5 py-1 text-xs font-medium rounded-full whitespace-nowrap", STEP_CLS[s])} data-testid={`ride-step-${m.id}`}>{STEP_LABELS[s]}</span>;
}

// Dispatch : « Pas encore reçue » / « Reçue à 09:12 » (course attribuée, ni terminée ni annulée).
export function ReceivedPill({ m }) {
  if (!m.employee_id || ["completed", "cancelled"].includes(m.status)) return null;
  return m.received_at
    ? <span className="px-2.5 py-1 text-xs font-medium rounded-full whitespace-nowrap text-emerald-700 bg-emerald-100 dark:bg-emerald-900/30 dark:text-emerald-300" data-testid={`ride-received-${m.id}`}>Reçue à {fmtTime(m.received_at)}</span>
    : <span className="px-2.5 py-1 text-xs font-medium rounded-full whitespace-nowrap text-amber-800 bg-amber-100 dark:bg-amber-900/30 dark:text-amber-300" data-testid={`ride-received-${m.id}`}>Pas encore reçue</span>;
}

// Une ligne de l'historique : « Sur place » ; « (par Nicole) » quand le dispatch a cliqué.
const logText = (e) => {
  const par = e.source === "dispatch" && e.by_name ? ` (par ${e.by_name})` : "";
  if (e.step === "received") return `Reçue${e.auto ? " d'office" : ""}`;
  if (e.step === "undone") return `Annulé : ${STEP_LABELS[e.of] ?? e.of}`;
  if (e.step === "reassigned") {
    // Lot T5a : première attribution et retrait du chauffeur dits clairement (noms déjà notés par le serveur).
    if (!e.from_name && e.to_name) return `Attribuée à ${e.to_name}${par}`;
    // Lot T5b : chauffeur retiré par une indisponibilité (« à réattribuer »).
    if (e.from_name && !e.to_name && e.cause === "unavailable") return `Chauffeur indisponible (avant : ${e.from_name})${par}`;
    if (e.from_name && !e.to_name) return `Chauffeur retiré (avant : ${e.from_name})${par}`;
    return `Réattribuée${e.from_name ? ` (avant : ${e.from_name})` : ""}${par}`;
  }
  if (e.step === "reactivated") return `Réactivée${par}`;
  return `${STEP_LABELS[e.step] ?? e.step}${par}`;
};

// Dispatch : frise des heures (Reçue, étapes, attente, position) et historique complet avec les noms.
export function StepTimeline({ m }) {
  const log = Array.isArray(m.step_log) ? m.step_log : [];
  if (!log.length) return null;
  const t = m.step_times || {};
  const etapes = [m.received_at && `Reçue ${fmtTime(m.received_at)}`,
    ...STEP_ORDER.filter((s) => s && t[s]).map((s) => `${STEP_LABELS[s]} ${fmtTime(t[s])}`)].filter(Boolean);
  const attente = waitMinutes(m);
  const pos = m.on_site_position;
  return (
    <div className="text-xs text-muted-foreground space-y-1" data-testid={`ride-timeline-${m.id}`}>
      <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5">
        {etapes.length > 0 && <span className="text-foreground">{etapes.join(" · ")}</span>}
        {attente != null && <span data-testid={`ride-wait-${m.id}`}>— attente {attente} min</span>}
        {pos && <a href={`https://www.google.com/maps?q=${pos.lat},${pos.lon}`} target="_blank" rel="noopener noreferrer" className="text-accent underline-offset-2 hover:underline whitespace-nowrap" data-testid={`ride-position-${m.id}`}>
          <MapPin className="inline h-3 w-3 mr-0.5" />position à l'arrivée{pos.accuracy != null ? ` (± ${Math.round(pos.accuracy)} m)` : ""}</a>}
      </div>
      <details>
        <summary className="cursor-pointer select-none py-1 w-fit" data-testid={`ride-history-${m.id}`}>Historique ({log.length})</summary>
        <ul className="pl-3 space-y-0.5">
          {log.map((e, i) => <li key={i}>{fmtDate(e.at)} {fmtTime(e.at)} — {logText(e)}{e.by_name && e.source !== "dispatch" ? ` — ${e.by_name}` : ""}</li>)}
        </ul>
      </details>
    </div>
  );
}

// Dispatch : corriger l'étape (en avant ou en arrière), après confirmation ; la correction est notée à son nom.
export function CorrectStep({ m, onDone }) {
  const [target, setTarget] = useState(null);
  if (m.status === "cancelled") return null;
  const step = stepOf(m);
  const confirm = async () => {
    const t = target; setTarget(null);
    try { await api.post(`/missions/${m.id}/step`, { step: t }); toast.success(`Étape corrigée : ${STEP_LABELS[t]}`); onDone?.(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  return (
    <>
      {/* « À faire » a pour valeur "" côté serveur ; une option de liste ne peut pas être vide : "todo" ici. */}
      <Select value="" onValueChange={(v) => setTarget(v === "todo" ? "" : v)}>
        <SelectTrigger className="h-8 w-auto gap-1 text-xs" data-testid={`ride-correct-${m.id}`}><SelectValue placeholder="Corriger l'étape" /></SelectTrigger>
        <SelectContent>
          {STEP_ORDER.filter((s) => s !== step).map((s) => <SelectItem key={s || "todo"} value={s || "todo"} data-testid={`ride-correct-${m.id}-${s || "todo"}`}>{STEP_LABELS[s]}</SelectItem>)}
        </SelectContent>
      </Select>
      <AlertDialog open={target !== null} onOpenChange={(o) => { if (!o) setTarget(null); }}>
        <AlertDialogContent data-testid="correct-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display">{`Passer la course à « ${STEP_LABELS[target ?? ""]} » ?`}</AlertDialogTitle>
            <AlertDialogDescription>Les heures des étapes suivantes seront effacées. La correction est notée à votre nom dans l'historique.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="correct-confirm-no">Retour</AlertDialogCancel>
            <AlertDialogAction onClick={confirm} data-testid="correct-confirm-yes">Corriger</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

// Attente de la course (lot T2), pour le dispatch et la secrétaire (le serveur ne l'envoie jamais au chauffeur) : proposée
// une fois le client à bord ou déclaré absent, ajoutée au prix seulement sur « Ajouter au prix » (jamais d'office).
// `compact` : version courte du tableau de dispatch.
export function WaitingOffer({ m, onDone, compact = false }) {
  const { settings } = useSettings();
  const [busy, setBusy] = useState(false);
  const w = m.waiting;
  if (!w || m.status === "cancelled") return null;
  const cur = settings?.currency || "EUR";
  const id = `ride-waiting-${m.id}`;
  if (w.already_added) {
    return <span className="inline-flex items-center gap-1 text-xs text-emerald-700 dark:text-emerald-300" data-testid={`${id}-added`}>
      <Check className="h-3.5 w-3.5" /> Attente ajoutée au prix</span>;
  }
  if (!w.amount) {
    return <span className="text-xs text-muted-foreground" data-testid={`${id}-free`}>
      {compact ? `Attente ${w.minutes} min (franchise)` : `Attente ${w.minutes} min : dans la franchise de ${w.free_minutes} min${w.airport ? " (aéroport)" : ""}`}</span>;
  }
  const add = async () => {
    setBusy(true);
    try { await api.post(`/rides/${m.id}/waiting/add`); toast.success(`Attente ajoutée au prix (${money(w.amount, cur)})`); onDone?.(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const detail = `${w.slices} × ${w.slice_minutes} min au-delà de la franchise de ${w.free_minutes} min${w.airport ? " (aéroport)" : ""}`;
  const facturee = (m.invoiced || []).length > 0; // lot T6 : prix verrouillé, l'attente ne s'ajoute plus
  return (
    <span className="inline-flex items-center gap-2 flex-wrap text-xs" data-testid={id}>
      <span className="font-medium text-amber-800 dark:text-amber-300" title={compact ? detail : undefined}>
        {compact ? `Attente ${w.minutes} min · ${money(w.amount, cur)}` : `Attente ${w.minutes} min : ${detail} = ${money(w.amount, cur)}`}</span>
      {facturee
        ? <span className="text-muted-foreground" data-testid={`${id}-billed`}>course facturée : prix verrouillé</span>
        : <Button size="sm" variant="outline" className="h-7 px-2 text-xs" disabled={busy} onClick={add} data-testid={`${id}-add`}>Ajouter au prix</Button>}
    </span>
  );
}

// Suppléments compris dans le prix d'une course (lot T2) : « dont 30,00 € de suppléments » sous le total.
export function supplementsOf(m) {
  return Math.round((m.price_details?.supplements || []).reduce((t, s) => t + (Number(s.amount) || 0), 0) * 100) / 100;
}

const lienRond = "inline-flex items-center gap-1.5 h-10 px-3.5 rounded-full border border-border text-sm hover:bg-muted whitespace-nowrap";

// Chauffeur : Reçu, étape suivante, Client absent, annuler (2 minutes), appels, GPS, « À encaisser ».
// Le prix n'arrive jamais au chauffeur (serveur) : seul `to_collect` (le passager paie) est affiché.
export function DriverPanel({ m, userId, clientPhone, dispatchPhone, places = [], onChange }) {
  const { settings } = useSettings();
  const [busy, setBusy] = useState(false);
  const [now, setNow] = useState(Date.now());
  const step = stepOf(m);
  const annulee = m.status === "cancelled";
  const finie = step === "no_show" || step === "done";
  const journal = (Array.isArray(m.step_log) ? m.step_log : []).filter((e) => e.step !== "received");
  const last = journal[journal.length - 1];
  const annulable = !!last && last.step === step && last.step in STEP_LABELS && last.by === userId && last.source === "chauffeur" && !annulee;
  // Horloge du téléphone en retard sur le serveur : jamais plus de 2:00 affichées (le serveur décide de toute façon).
  const reste = annulable ? Math.min(UNDO_SECONDS, UNDO_SECONDS - Math.floor((now - new Date(last.at)) / 1000)) : 0;
  useEffect(() => {
    if (reste <= 0) return undefined;
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, [reste > 0]); // eslint-disable-line react-hooks/exhaustive-deps

  const send = async (chemin, body, ok) => {
    if (busy) return null;
    setBusy(true);
    try {
      const r = await api.post(`/missions/${m.id}/${chemin}`, body || {});
      if (ok) toast.success(ok);
      setNow(Date.now());
      onChange?.();
      return r.data;
    } catch (e) {
      toast.error(e.response ? apiError(e.response.data?.detail) : "Pas de réseau : réessayez.");
      return null;
    } finally { setBusy(false); }
  };
  const go = async (cible) => {
    const r = await send("step", { step: cible }, `Étape : ${STEP_LABELS[cible]}`);
    // « Sur place » : position du téléphone, une seule fois, sans jamais bloquer (refus ou 10 s sans réponse : rien).
    if (r && cible === "on_site" && typeof navigator !== "undefined" && navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (p) => api.post(`/missions/${m.id}/position`, { lat: p.coords.latitude, lon: p.coords.longitude, accuracy: p.coords.accuracy })
          .then(() => onChange?.()).catch(() => {}),
        () => {}, { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 });
    }
  };

  const appels = [["Passager", m.passenger_phone, "passenger"], ["Donneur d'ordre", clientPhone, "orderer"], ["Dispatch", dispatchPhone, "dispatch"]]
    .filter(([, tel]) => (tel || "").trim());
  const gps = [["pickup", "Prise en charge"], ["dropoff", "Dépose"]]
    .map(([w, l]) => [w, l, placeAddress(m, places, w)]).filter(([, , a]) => a);
  const suivantes = DRIVER_NEXT[step] || [];
  const mmss = `${Math.floor(reste / 60)}:${String(reste % 60).padStart(2, "0")}`;
  return (
    <div className="mt-3 space-y-3" data-testid={`driver-panel-${m.id}`}>
      {m.to_collect != null && (
        <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-xl bg-amber-100 text-amber-900 dark:bg-amber-900/30 dark:text-amber-200 font-medium" data-testid={`driver-collect-${m.id}`}>
          <Wallet className="h-4 w-4" /> À encaisser : {money(m.to_collect, settings?.currency || "EUR")}
        </div>
      )}
      {!annulee && !finie && (
        <div className="flex flex-wrap gap-2">
          {!m.received_at && <Button className="h-11 rounded-full gap-2 px-5" disabled={busy} onClick={() => send("received", {}, "Course reçue")} data-testid={`driver-received-${m.id}`}><Check className="h-4 w-4" /> Reçu</Button>}
          {suivantes.map((s, i) => (
            <Button key={s} className="h-11 rounded-full px-5" disabled={busy} onClick={() => go(s)} data-testid={`driver-step-${s}-${m.id}`}
              variant={s === "no_show" ? "destructive" : (i === 0 && m.received_at ? "default" : "outline")}>{STEP_LABELS[s]}</Button>
          ))}
        </div>
      )}
      {reste > 0 && (
        <Button variant="ghost" size="sm" className="gap-1.5 h-9" disabled={busy} onClick={() => send("undo", {}, `« ${STEP_LABELS[step]} » annulé`)} data-testid={`driver-undo-${m.id}`}>
          <Undo2 className="h-4 w-4" /> Annuler « {STEP_LABELS[step]} » ({mmss})
        </Button>
      )}
      {appels.length > 0 && (
        <div className="flex flex-wrap gap-2" data-testid={`driver-calls-${m.id}`}>
          {appels.map(([l, tel, k]) => <a key={k} href={telHref(tel)} className={lienRond} data-testid={`driver-call-${k}-${m.id}`}><Phone className="h-4 w-4 text-accent" /> {l}</a>)}
        </div>
      )}
      {!annulee && !finie && gps.map(([w, l, a]) => (
        <div key={w} className="flex flex-wrap items-center gap-2" data-testid={`driver-gps-${w}-${m.id}`}>
          <span className="text-xs text-muted-foreground w-full sm:w-28">{l}</span>
          <a href={wazeUrl(a)} target="_blank" rel="noopener noreferrer" className={lienRond} data-testid={`driver-waze-${w}-${m.id}`}><Navigation className="h-4 w-4 text-accent" /> Waze</a>
          <a href={mapsUrl(a)} target="_blank" rel="noopener noreferrer" className={lienRond} data-testid={`driver-maps-${w}-${m.id}`}><MapPin className="h-4 w-4 text-accent" /> Google Maps</a>
        </div>
      ))}
    </div>
  );
}
